import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObs } from '../../contexts/ObsContext';
import { Hint, Btn, SecPill, StudentTag } from '../../components/ui';
import { BUCKETS, WHO_OPTIONS } from '../../theme';
import { toast } from 'react-toastify';
import useRequireObs from '../../lib/useRequireObs';

const WHO_LABEL = Object.fromEntries(WHO_OPTIONS.map(o => [o.value, o.label]));

const WHO_RANK = { most:4, half:3, few:2, one:1 };
const rank = arr => [...arr].sort((a,b) => (WHO_RANK[b.who]||0)-(WHO_RANK[a.who]||0));

function LevCard({ card, selected, onToggle, type, topRanked }) {
  const b = BUCKETS[card.bucket] || {};
  return (
    <div
      className={`lev-card lev-${type}${selected ? ' sel' : ''}`}
      onClick={onToggle}
    >
      <div className="lev-check">
        {selected && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div className="lev-body">
        {card.studentId && <StudentTag name={card.studentId} />}
        <div className="lev-text">{card.statement}</div>
        <div className="lev-meta">
          {topRanked && <span className="lev-ai">Highest leverage</span>}
          {b.name && (
            <span className="lev-bucket-tag"
              style={{ background: type==='best' ? 'var(--teal-l)' : 'var(--purple-l)', color: type==='best' ? 'var(--teal-d)' : 'var(--purple-d)' }}>
              {b.name}
            </span>
          )}
          <span className="lev-who-tag">
            {WHO_LABEL[card.who] || ''}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function LeveragePage() {
  const navigate = useNavigate();
  const { statements, finalBest, finalBetter, setFinalBest, setFinalBetter } = useObs();
  useRequireObs();

  // Selecting from the statements themselves, so what is chosen here is
  // exactly what gets said in the debrief.
  const cards = useMemo(() => statements.map(s => ({
    id: s.note_id,
    statement: s.statement,
    bucket: s.bucket,
    who: s.student_group,
    studentId: s.student_identifier || null,
    side: s.side,
    evidence: s.evidence_text,
    impact: s.impact_text,
  })), [statements]);

  const rankedBest   = useMemo(() => rank(cards.filter(c => c.side === 'pos')), [cards]);
  const rankedBetter = useMemo(() => rank(cards.filter(c => c.side === 'neg')), [cards]);

  // Coming back to this page — from the debrief, or after a refresh — shows
  // what was chosen, not the suggestions again. A finding chosen earlier that
  // is no longer in the statements is dropped. Only with nothing chosen yet
  // does the page start from the suggestions.
  const [initial] = useState(() => {
    const live = new Set(cards.map(c => c.id));
    const best   = finalBest.map(c => c.id).filter(id => live.has(id));
    const better = finalBetter.map(c => c.id).filter(id => live.has(id));
    if (best.length || better.length) return { best, better };
    return { best:   rankedBest.slice(0, 2).map(c => c.id),
             better: rankedBetter.slice(0, 1).map(c => c.id) };
  });
  const [selBest,   setSelBest]   = useState(() => new Set(initial.best));
  const [selBetter, setSelBetter] = useState(() => new Set(initial.better));

  // Every change is kept straight away, so leaving by Back keeps it too.
  useEffect(() => {
    setFinalBest(rankedBest.filter(c => selBest.has(c.id)));
    setFinalBetter(rankedBetter.filter(c => selBetter.has(c.id)));
  }, [selBest, selBetter, rankedBest, rankedBetter, setFinalBest, setFinalBetter]);


  const toggle = (set, setFn, id, max) => {
    const s = new Set(set);
    if (!s.has(id) && s.size >= max) { toast.error(`Maximum ${max} selections for this section.`); return; }
    s.has(id) ? s.delete(id) : s.add(id);
    setFn(s);
  };

  const handleStart = () => {
    const best   = rankedBest.filter(c => selBest.has(c.id));
    const better = rankedBetter.filter(c => selBetter.has(c.id));
    // The debrief script is these statements — with none selected there is
    // nothing to read out.
    if (best.length === 0 && better.length === 0) {
      toast.error('Select at least one finding to take into the debrief.');
      return;
    }
    setFinalBest(best);
    setFinalBetter(better);
    navigate('/observe/debrief');
  };

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/observe/conclusions')}>← Back</button>
        <div className="obsbar-title">Debrief focus</div>
      </div>

      <div className="scroll">
        <div className="pad">
          <Hint>The app has suggested the highest leverage findings. You can add or remove. You have the final say.</Hint>

          {/* Best */}
          <div className="count-row">
            <SecPill bg="var(--teal-l)" color="var(--teal-d)">Learning was best for…</SecPill>
            <span style={{ fontSize:12, fontWeight:500, color:'var(--teal)' }}>{selBest.size} / 8 max</span>
          </div>
          {rankedBest.map((card, i) => (
            <LevCard key={card.id} card={card} type="best" topRanked={i===0}
              selected={selBest.has(card.id)}
              onToggle={() => toggle(selBest, setSelBest, card.id, 8)} />
          ))}

          <div style={{ height: 12 }} />

          {/* Better */}
          <div className="count-row">
            <SecPill bg="var(--purple-l)" color="var(--purple-d)">Learning could have been better for…</SecPill>
            <span style={{ fontSize:12, fontWeight:500, color:'var(--purple)' }}>{selBetter.size} / 3 max</span>
          </div>
          {rankedBetter.map((card, i) => (
            <LevCard key={card.id} card={card} type="better" topRanked={i===0}
              selected={selBetter.has(card.id)}
              onToggle={() => toggle(selBetter, setSelBetter, card.id, 3)} />
          ))}

          <div style={{ background:'var(--surface)', borderRadius:'var(--rs)', padding:'10px 12px', margin:'12px 0', fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
            <strong style={{ color:'var(--text)' }}>Why these were selected:</strong> Findings that affected most students are ranked highest. Specific student moments are included where they illustrate a broader pattern.
          </div>

          <Btn variant="blue" onClick={handleStart}>Start debrief with these findings →</Btn>
        </div>
      </div>
    </div>
  );
}
